import React from 'react';
import { Helmet } from 'react-helmet-async'; 
import { Link } from 'react-router-dom';

const AboutPage = () => {
  // SEO: Organization Schema for Brand Authority
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "AyushVatika",
    "url": "https://www.ayushvatika.com",
    "logo": "https://www.ayushvatika.com/logo.png",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+91-123-456-7890",
      "contactType": "customer service",
      "areaServed": "IN",
      "availableLanguage": ["en", "hi"]
    },
    "sameAs": [
      "https://facebook.com/ayushvatika",
      "https://instagram.com/ayushvatika"
    ]
  };

  return (
    <>
      <Helmet>
        <title>About AyushVatika | Best Ayurvedic Store & Herbal Products in India</title>
        <meta 
          name="description" 
          content="AyushVatika connects ancient Ayurveda with modern wellness. We are India's trusted source for authentic herbal medicines, organic supplements, and natural skin care."
        />
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white text-gray-800">
        {/* Banner Section */}
        <div className="relative h-[60vh] w-full overflow-hidden">
          <img
            src="/aboutbanner.webp"
            alt="AyushVatika Team - Authentic Ayurvedic Medicine Experts" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-transparent via-black/40 to-black/70" />
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2">
            <Link to="/blog">
  <button className="px-8 py-4 bg-green-700 text-white text-lg font-semibold rounded-full shadow-lg hover:bg-green-800 transition-all duration-300 ease-in-out transform hover:scale-105">
    Discover Our Ayurvedic Journey
  </button>
</Link>
          </div>
        </div>

        {/* Introduction Section */}
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold text-green-800 mb-6 leading-tight">
            AyushVatika: Authentic Ayurveda for Modern India
          </h1>
          <p className="text-xl leading-relaxed text-gray-700 max-w-4xl mx-auto">
            At AyushVatika, we bridge the gap between <strong>Vedic herbal wisdom</strong> and modern lifestyle needs. As a leading <strong>Ayurvedic product manufacturer</strong>, our solutions nourish both body and soul naturally.
          </p>
        </div>

        {/* Who We Are Section */}
        <div className="max-w-7xl mx-auto px-4 py-16 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="w-full order-2 md:order-1">
            <img
              src="/blog2.png"
              alt="AyushVatika Herbal Processing and Natural Ingredients"
              className="rounded-3xl shadow-2xl w-full h-auto object-cover transform hover:scale-105 transition-transform duration-500 ease-in-out"
            />
          </div>

          <div className="order-1 md:order-2">
            <h2 className="text-3xl md:text-4xl font-bold text-green-800 mb-6">
              Who We Are: Roots in Nature
            </h2>
            <p className="text-lg leading-relaxed text-gray-700 mb-4">
              AyushVatika's journey began with a vision: to make the <strong>healing power of Indian herbs</strong> accessible to every home. We combine centuries-old recipes with modern research to create products that are <strong>100% organic and chemical-free</strong>.
            </p>
            <p className="mt-4 text-lg text-gray-700">
              Every product is a testament to our dedication to herbal efficacy and environmental stewardship.
            </p>
          </div>
        </div>

        {/* Our Mission Section */}
        <div className="bg-green-50 py-16">
          <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="text-center md:text-left">
              <h2 className="text-3xl md:text-4xl font-bold text-green-800 mb-6">
                Our Mission: Wellness for All
              </h2>
              <ul className="list-disc list-inside text-lg text-gray-700 mt-4 space-y-2">
                <li>Delivering <strong>premium quality Ayurvedic medicines</strong>.</li>
                <li>Promoting preventive health through natural remedies.</li>
                <li>Ensuring sustainable sourcing from Indian farmers.</li>
              </ul>
            </div>
            <div className="w-full">
              <img
                src="https://www.itoozhiayurveda.in/wp-content/uploads/2024/07/IMG-20240625-WA0005-720x480.jpg" 
                alt="Ayurvedic Doctor Consultation and Herbal Therapy"
                className="rounded-3xl shadow-2xl w-full h-auto object-cover transform hover:scale-105 transition-transform duration-500 ease-in-out"
              />
            </div>
          </div>
        </div>

        {/* Our Values Section */}
        <div className="max-w-7xl mx-auto px-4 py-16">
          <h2 className="text-3xl md:text-4xl font-bold text-green-800 text-center mb-12">
            Why Trust AyushVatika?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="flex flex-col items-center text-center p-6 bg-white rounded-xl shadow-lg border border-gray-100">
              <h3 className="text-xl font-semibold text-green-900 mb-3">Authenticity (Asli Ayurveda)</h3>
              <p className="text-gray-700">
                We preserve the true essence of Ayurveda. Our products are made from genuine herbs.
              </p>
            </div>
            <div className="flex flex-col items-center text-center p-6 bg-white rounded-xl shadow-lg border border-gray-100">
              <h3 className="text-xl font-semibold text-green-900 mb-3">Purity (Shuddhta)</h3>
              <p className="text-gray-700">
                No harmful chemicals, parabens, or sulfates. Only nature's best ingredients.
              </p>
            </div>
            <div className="flex flex-col items-center text-center p-6 bg-white rounded-xl shadow-lg border border-gray-100">
              <h3 className="text-xl font-semibold text-green-900 mb-3">Sustainability</h3>
              <p className="text-gray-700">
                Eco-friendly packaging and ethical sourcing for a greener planet.
              </p>
            </div>
          </div>
        </div>

        {/* Team Section remains similar but ensure Alts are descriptive as per previous code */}
      </div>
    </>
  );
};

export default AboutPage;